import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import StopIcon from "@mui/icons-material/Stop";
import SyncIcon from "@mui/icons-material/Sync";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  applyLatestManagedRunPolicy,
  attachManagedRun,
  getManagedRunForWorkflowSession,
  stopManagedRun,
} from "../ManagedRunPolicy/managedRunApi";
import CustomPaper from "../../common/CustomPaper";
import ConfirmApplyManagedRunPolicyDialog from "./ConfirmApplyManagedRunPolicyDialog";
import ConfirmStopManagedRunDialog from "./ConfirmStopManagedRunDialog";
import "./WorkflowProgressView.css";

export default function ManagedRunOperatorPanel({
  projectId,
  projectSessionId,
  isTest,
  workflowExtras,
  onOpenChat,
}) {
  const [loading, setLoading] = useState(false);
  const [managedRun, setManagedRun] = useState(null);
  const [error, setError] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [applyingPolicy, setApplyingPolicy] = useState(false);
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState("");

  const policyEnabled = workflowExtras?.managedRunPolicy?.enabled === true;

  const refresh = useCallback(async ({ preserveAttachedChat = false } = {}) => {
    if (isTest || !projectSessionId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getManagedRunForWorkflowSession(projectSessionId);
      const nextManagedRun =
        response?.data?.attached === false ? null : response?.data;
      setManagedRun((current) =>
        preserveAttachedChat && current?.chatSessionId && !nextManagedRun?.chatSessionId
          ? current
          : nextManagedRun || null
      );
    } catch (e) {
      if (e?.response?.status === 404) {
        setManagedRun((current) =>
          preserveAttachedChat && current?.chatSessionId ? current : null
        );
      } else {
        setError("Unable to load managed-run operator status.");
      }
    } finally {
      setLoading(false);
    }
  }, [isTest, projectSessionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (isTest) return null;

  const hasAttachedChat = Boolean(managedRun?.chatSessionId);
  const openManagedRunChat = () => {
    if (!hasAttachedChat) return;
    onOpenChat?.(managedRun);
  };

  const handleAttach = async () => {
    setAttaching(true);
    setError(null);
    try {
      const response = await attachManagedRun(projectId, projectSessionId);
      setManagedRun(response?.data || null);
      setSnackbar("Managed-run operator attached.");
      await refresh({ preserveAttachedChat: true });
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          "Unable to attach managed-run operator. Check that managed runs are enabled on the latest workflow definition."
      );
    } finally {
      setAttaching(false);
    }
  };

  const handleStop = async () => {
    if (!managedRun?.chatSessionId) return;
    setStopping(true);
    setError(null);
    try {
      await stopManagedRun(managedRun.chatSessionId);
      setStopDialogOpen(false);
      setSnackbar("Managed-run operation stopped.");
      await refresh();
    } catch (e) {
      setError("Unable to stop managed-run operation.");
    } finally {
      setStopping(false);
    }
  };

  const handleApplyPolicy = async () => {
    setApplyingPolicy(true);
    setError(null);
    try {
      await applyLatestManagedRunPolicy(projectId, projectSessionId);
      setApplyDialogOpen(false);
      setSnackbar("Managed-run policy refreshed.");
      await refresh();
    } catch (e) {
      setError("Unable to apply latest managed-run policy.");
    } finally {
      setApplyingPolicy(false);
    }
  };

  return (
    <CustomPaper className="managed-run-operator-panel">
      <SmartToyIcon color={managedRun ? "primary" : "disabled"} />
      <Box className="managed-run-operator-panel__content">
        <Typography variant="subtitle2">Managed-run operator</Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading operator status...
          </Typography>
        ) : hasAttachedChat ? (
          <Typography variant="body2" color="text.secondary">
            Operator attached ·{" "}
            <Link
              component="button"
              type="button"
              className="managed-run-operator-panel__chat-link"
              onClick={openManagedRunChat}
            >
              Open chat session
            </Link>
            {managedRun.managedRunPolicyTemplateId
              ? ` · ${managedRun.managedRunPolicyTemplateId}`
              : ""}
          </Typography>
        ) : policyEnabled ? (
          <Typography variant="body2" color="text.secondary">
            This workflow has managed runs enabled, but this session is not
            currently managed.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Attach an operator if managed runs are enabled on the latest
            workflow definition.
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="warning" className="managed-run-operator-panel__alert">
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        {loading && <CircularProgress size={18} />}
        {!loading && !hasAttachedChat && (
          <Button size="small" onClick={handleAttach} disabled={attaching}>
            Attach operator
            {attaching && (
              <CircularProgress
                size={16}
                className="managed-run-operator-panel__inline-spinner"
              />
            )}
          </Button>
        )}
        {!loading && hasAttachedChat && (
          <>
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={openManagedRunChat}
            >
              Open chat
            </Button>
            <Button
              size="small"
              startIcon={<SyncIcon />}
              onClick={() => setApplyDialogOpen(true)}
              disabled={applyingPolicy}
            >
              Apply latest policy
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<StopIcon />}
              onClick={() => setStopDialogOpen(true)}
              disabled={stopping}
            >
              Stop management
            </Button>
          </>
        )}
      </Stack>

      <ConfirmStopManagedRunDialog
        open={stopDialogOpen}
        loading={stopping}
        onCancel={() => setStopDialogOpen(false)}
        onConfirm={handleStop}
      />
      <ConfirmApplyManagedRunPolicyDialog
        open={applyDialogOpen}
        loading={applyingPolicy}
        onCancel={() => setApplyDialogOpen(false)}
        onConfirm={handleApplyPolicy}
      />
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3000}
        onClose={() => setSnackbar("")}
        message={snackbar}
      />
    </CustomPaper>
  );
}
